import { pick } from 'lodash';

import { PointInfo } from '../../typings/banners';

type GeocodeResult = ymaps.GeocodeResult;

export interface Address {
    country?: string;
    city?: string;
    street?: string;
    house?: string;
}

export interface AddressGeoObjectWithPointInfo extends AddressGeoObject {
    pointOnMap?: PointInfo;
}

export interface AddressGeoObject {
    geoId: number;
    address: Address;
}

export enum AddressField {
    country = 'country',
    city = 'city',
    street = 'street',
    house = 'house'
}

export const addressFieldWeight = {
    country: 0,
    city: 1,
    street: 2,
    house: 3
};

export const addressFieldHash = {
    country: 'country',
    city: 'locality',
    street: 'street',
    house: 'house'
};

export const mapApiShiftBoundByCurrentLevel = {
    country: 2,
    city: 0.8,
    street: 0.001,
    house: 0.0007
};

export const mapApiZoomByCurrentLevel = {
    country: 4,
    city: 6,
    street: 9,
    house: 10
};

export function getAddressString(address: Address, cutAddressBy: AddressField): string {
    const listAddress = Object.keys(address).reduce((result, keyCurrent) => {
        if (address[keyCurrent] && addressFieldWeight[keyCurrent] <= addressFieldWeight[cutAddressBy]) {
            return result.concat(address[keyCurrent]);
        }
        return result;
    }, []);
    return listAddress.join(', ');
}

export function getAddressFieldByAddressFieldHashForGeoRequest(addressKeyCurrentLevel: AddressField): string[] {
    const addressField = addressFieldHash[addressKeyCurrentLevel];

    return [addressField];
}

export function getAddressObject(geoObject: GeocodeResult, cutAddressBy?: AddressField): Address {
    const administrativeAreas = geoObject.getAdministrativeAreas();
    const localities = geoObject.getLocalities().slice(0, 1);

    if (localities.length === 0 && administrativeAreas[0]) {
        localities.push(administrativeAreas[0]);
    }

    const addressObj = {
        country: geoObject.getCountry(),
        city: localities.join(', '),
        street: geoObject.getThoroughfare(),
        house: geoObject.getPremiseNumber()
    };

    let listKeysAddressField: string[] = [];
    if (cutAddressBy) {
        listKeysAddressField = Object.keys(addressObj).filter(key => {
            return addressFieldWeight[key] <= addressFieldWeight[cutAddressBy];
        });
    }

    return pick(addressObj, listKeysAddressField) as Address;
}

export function getShiftBoundsByCurrentLevel(addressKeyCurrentLevel: AddressField): number {
    return mapApiShiftBoundByCurrentLevel[addressKeyCurrentLevel];
}

export function getZoomByCurrentLevel(addressKeyCurrentLevel: AddressField): number {
    return mapApiZoomByCurrentLevel[addressKeyCurrentLevel];
}

export function getGeoId(geoObject: GeocodeResult): number {
    return geoObject.properties.get('metaDataProperty.GeocoderMetaData.InternalToponymInfo.geoid');
}

export function getFullAddressText(address: Address): string {
    const { country, city, house, street } = address;

    return [country, city, street, house].join(', ').trim();
}
